// pulse — anonymous usage signals for Bloom (no cookies; no IP is stored server-side).
// Module and endpoint names avoid words that ad-block lists match ("track", "analytics", "beacon").
const ENDPOINT = "/api/bloom/signal";
const SID_KEY = "bloom.sid";
const SRC_KEY = "bloom.src";

function sessionId(): string {
    try {
        let s = window.sessionStorage.getItem(SID_KEY);
        if (!s) {
            s = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
            window.sessionStorage.setItem(SID_KEY, s);
        }
        return s;
    } catch (e) {
        return "";
    }
}

// utm_source survives in-page navigation for the whole visit.
// A visit that lands on a check result (?c=) with no utm_source came from a pasted link
// (the address bar holds ?c= after a check), so it counts as "link" instead of direct.
function source(): string {
    try {
        const p = new URLSearchParams(window.location.search);
        const q = p.get("utm_source");
        if (q) {
            window.sessionStorage.setItem(SRC_KEY, q.slice(0, 40));
            return q.slice(0, 40);
        }
        const kept = window.sessionStorage.getItem(SRC_KEY);
        if (kept) return kept;
        if (p.get("c")) {
            window.sessionStorage.setItem(SRC_KEY, "link");
            return "link";
        }
        return "";
    } catch (e) {
        return "";
    }
}

// app_tap: a "Get the app" / "Add to home screen" tap (s = where and which install path);
// app_installed: the browser confirmed the install.
export type SignalType = "view" | "sheet_open" | "share_tap" | "check_open" | "check_run" | "report_open" | "amazon_tap" | "app_tap" | "app_installed";

export function signal(t: SignalType, extra: { a?: string; c?: string; ch?: string; l?: string; s?: string } = {}) {
    try {
        const body = JSON.stringify({
            t,
            sid: sessionId(),
            s: source(),
            l: document.documentElement.lang || "en",
            r: t === "view" ? document.referrer : "",
            ...extra,
        });
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
        fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    } catch (e) {}
}

// Removes utm_* from the address bar after the first view so shared links stay clean.
// GT-1 (2026-11-03): OBTO's consent loader reads the address for the GA4 / Meta page view, and both
// count every address change as a new page view. So once measurement is allowed the tags stay (a strip
// would count the landing twice). They go when the visitor says no, under DNT / GPC, or after 60 s
// without an answer.
export function stripUtm() {
    const strip = () => {
        try {
            const u = new URL(window.location.href);
            let changed = false;
            ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
                if (u.searchParams.has(k)) { u.searchParams.delete(k); changed = true; }
            });
            if (changed) window.history.replaceState(null, "", u.toString());
        } catch (e) {}
    };
    const consent = () => {
        try {
            const n = navigator as any;
            if (n.doNotTrack === "1" || n.globalPrivacyControl === true) return "denied";
            return localStorage.getItem("obto_marketing_measurement_consent_v2") || "";
        } catch (e) { return "denied"; }
    };
    let tries = 0;
    const tick = () => {
        const c = consent();
        if (c === "granted") return;
        if (c === "denied" || ++tries >= 120) strip(); else setTimeout(tick, 500);
    };
    tick();
}
